import { useQuery } from "@tanstack/react-query";
import { CACHE_KEYS } from "@api/constants/cacheKeys";
import useGlobalDebounceHandler from "./useGlobalDebounceHandler";
import { useState } from "react";
import { IGetServiceProps } from "@api/types/globalService.types";
import { handleGlobalGetRequestQuery } from "@api/functions/getRequests";
import URLS from "@api/constants/urls";
import { urlUtility } from "@repo/utilities/url.utility.ts";

export const useGetAllCustomerJourneyServices = ({
  enableQuery = true,
  page = "1",
  per_page = "15",
  showActiveOnly = true,
}: IGetServiceProps) => {
  //States
  const [searchQuery, setSearchQuery] = useState("");

  //Hooks
  const { debouncedQuery } = useGlobalDebounceHandler(searchQuery);
  const urlQueries = urlUtility.getUrlQueries(location.search);

  //Variables
  const partnerId = urlQueries?.["partner-id"] || "";
  const journeyFor = urlQueries?.["journey-for"] || "";
  const journeyTypeId = urlQueries?.["journey-type-id"] || "";
  const created_between = urlQueries["created_between"] || "";
  const allCustomerJourneyServices = useQuery({
    queryKey: [
      ...CACHE_KEYS.JOURNEYS,
      page,
      debouncedQuery,
      per_page,
      showActiveOnly,
      partnerId,
      journeyFor,
      created_between,
      journeyTypeId,
    ],

    queryFn: () => {
      const searchParams = {
        "filter[name]": debouncedQuery,
        "filter[partner_id]": partnerId,
        "filter[journey_for]": journeyFor,
        "filter[created_between]": created_between,
        "filter[journey_type_id]": journeyTypeId,
        per_page,
        page,
        include: "partner,plans,journey_type",
        ...(showActiveOnly && { "filter[is_active]": true }),
      };
      return handleGlobalGetRequestQuery({
        url: URLS.JOURNEYS,
        searchParams,
      });
    },
    enabled: enableQuery,
  });

  return {
    services: { allCustomerJourneyServices },
    states: { searchQuery, setSearchQuery },
  };
};

export const useGetSingleJourneyLinkService = () => {
  const urlQueries = urlUtility.getUrlQueries(location.search);
  const journeyLinkId = urlQueries?.["journey-link-id"] || "";

  const singleJourneyLinkService = useQuery({
    queryKey: [...CACHE_KEYS.JOURNEYS, journeyLinkId],
    queryFn: () => {
      return handleGlobalGetRequestQuery({
        url: `${URLS.JOURNEYS}/${journeyLinkId}`,
      });
    },
    enabled: !!journeyLinkId,
  });

  return { service: { singleJourneyLinkService } };
};
